/**
 * NotificationBell Component
 *
 * Displays a notification bell icon with a badge showing unread count.
 * Clicking opens a dropdown panel with recent notifications.
 *
 * Uses the useRequestNotifications hook for real-time WebSocket notifications.
 */
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Popover,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import React, { useCallback, useState } from "react";
import type { RequestNotification } from "../../hooks/useRequestNotifications";

export interface NotificationBellProps {
  /** List of notifications to display */
  notifications: RequestNotification[];
  /** Count of unread notifications */
  unreadCount: number;
  /** Callback when a notification is clicked */
  onNotificationClick?: (notification: RequestNotification) => void;
  /** Callback to mark a notification as read */
  onMarkAsRead?: (notificationId: string) => void;
  /** Callback to mark all notifications as read */
  onMarkAllAsRead?: () => void;
  /** Callback to view all notifications (navigate to notifications page) */
  onViewAll?: () => void;
  /** Callback to view only unread notifications */
  onViewUnread?: () => void;
  /** Whether to animate the bell when there are unread notifications */
  animate?: boolean;
  /** Maximum notifications to show in dropdown */
  maxVisible?: number;
}

const NotificationBell: React.FC<NotificationBellProps> = ({
  notifications,
  unreadCount,
  onNotificationClick,
  onMarkAsRead,
  onMarkAllAsRead,
  onViewAll,
  onViewUnread,
  animate = true,
  maxVisible = 5,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleOpen = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleNotificationClick = useCallback(
    (notification: RequestNotification) => {
      onNotificationClick?.(notification);
      if (!notification.read) {
        onMarkAsRead?.(notification.id);
      }
      handleClose();
    },
    [onNotificationClick, onMarkAsRead, handleClose],
  );

  const handleViewAll = useCallback(() => {
    handleClose();
    onViewAll?.();
  }, [onViewAll, handleClose]);

  const handleViewUnread = useCallback(() => {
    handleClose();
    onViewUnread?.();
  }, [onViewUnread, handleClose]);

  const open = Boolean(anchorEl);
  const id = open ? "notification-popover" : undefined;

  const visibleNotifications = notifications.slice(0, maxVisible);
  const hasMore = notifications.length > maxVisible;

  // Animation keyframes for the bell
  const bellAnimation =
    animate && unreadCount > 0
      ? {
          animation: "bellRing 2s ease-in-out infinite",
          "@keyframes bellRing": {
            "0%, 100%": { transform: "rotate(0deg)" },
            "10%": { transform: "rotate(15deg)" },
            "20%": { transform: "rotate(-15deg)" },
            "30%": { transform: "rotate(10deg)" },
            "40%": { transform: "rotate(-10deg)" },
            "50%": { transform: "rotate(5deg)" },
            "60%": { transform: "rotate(-5deg)" },
            "70%, 100%": { transform: "rotate(0deg)" },
          },
        }
      : {};

  return (
    <>
      <IconButton
        aria-describedby={id}
        onClick={handleOpen}
        aria-label={`Notificações${unreadCount > 0 ? `, ${unreadCount} não lidas` : ""}`}
        sx={{
          color: theme.palette.text.secondary,
          "&:hover": {
            color: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          max={99}
          sx={{
            "& .MuiBadge-badge": {
              fontSize: "0.7rem",
              minWidth: 18,
              height: 18,
            },
          }}
        >
          {unreadCount > 0 ? (
            <NotificationsActiveIcon sx={bellAnimation} />
          ) : (
            <NotificationsIcon />
          )}
        </Badge>
      </IconButton>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              width: 360,
              maxWidth: "90vw",
              mt: 1,
              borderRadius: 2,
              overflow: "hidden",
            },
          },
        }}
      >
        <Paper elevation={0}>
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              Notificações
            </Typography>
            {unreadCount > 0 && (
              <Button
                size="small"
                onClick={onMarkAllAsRead}
                sx={{ textTransform: "none" }}
              >
                Marcar todas como lidas
              </Button>
            )}
          </Box>

          {/* Notification List */}
          {visibleNotifications.length > 0 ? (
            <List disablePadding sx={{ maxHeight: 400, overflow: "auto" }}>
              {visibleNotifications.map((notification, index) => (
                <React.Fragment key={notification.id}>
                  <ListItem
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      cursor: "pointer",
                      py: 1.5,
                      px: 2,
                      backgroundColor: notification.read
                        ? "transparent"
                        : alpha(theme.palette.primary.main, 0.04),
                      "&:hover": {
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.08,
                        ),
                      },
                      transition: "background-color 0.2s ease",
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          fontWeight={notification.read ? 400 : 600}
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {notification.message}
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          {formatDistanceToNow(
                            new Date(notification.timestamp),
                            {
                              addSuffix: true,
                              locale: ptBR,
                            },
                          )}
                        </Typography>
                      }
                    />
                    {!notification.read && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: theme.palette.primary.main,
                          ml: 1,
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </ListItem>
                  {index < visibleNotifications.length - 1 && (
                    <Divider component="li" />
                  )}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Box
              sx={{
                py: 4,
                px: 2,
                textAlign: "center",
              }}
            >
              <NotificationsIcon
                sx={{
                  fontSize: 48,
                  color: theme.palette.text.disabled,
                  mb: 1,
                }}
              />
              <Typography variant="body2" color="text.secondary">
                Nenhuma notificação
              </Typography>
            </Box>
          )}

          {/* Footer */}
          {(hasMore || notifications.length > 0) && (
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderTop: `1px solid ${theme.palette.divider}`,
                textAlign: "center",
              }}
            >
              {unreadCount > 0 && (
                <Button
                  size="small"
                  onClick={handleViewUnread}
                  sx={{ textTransform: "none", mr: 1 }}
                >
                  Ver nao lidas ({unreadCount})
                </Button>
              )}
              <Button
                size="small"
                onClick={handleViewAll}
                sx={{ textTransform: "none" }}
              >
                {hasMore
                  ? `Ver todas (${notifications.length})`
                  : "Ver todas as notificações"}
              </Button>
            </Box>
          )}
        </Paper>
      </Popover>
    </>
  );
};

export default NotificationBell;
